<template>
  <div class="lower-container">
    <div class="floor-plan">
      <div class="floor-plan-title">{{ adminText('workflowsAll.floor_plan_d5786d3', 'Floor plan') }}</div>
      <div v-if="!isTableMode" class="radio-buttons-container">
        <custom-radio-button :label="adminText('workflowsAll.seats_692aab5', 'Seats')" :value="changeSelectOption === 1" @click="changeSelectOption = 1" />
        <custom-radio-button :label="adminText('workflowsAll.rows_52d0b35', 'Rows')" :value="changeSelectOption === 2" @click="changeSelectOption = 2" />
      </div>
      <div id="spinner-svg-wrapper">
        <spinner v-if="loadingSvg" />
        <p v-if="mapError" class="map-error">{{ mapError }}</p>
        <object
          id="gratis-svgfile"
          ref="svgfile"
          :data="initialVenue.imageUrl"
          type="image/svg+xml"
          style="width: 100%"
          @load="onSvgLoad"
          @error="onSvgError"
        />
      </div>
      <div class="seat-state-wrapper">
        <div v-for="entry in legend" :key="entry.label" class="seat-state">
          <div class="state-logo" :style="{ background: entry.color, border: entry.border }" />
          <span>{{ workflowLabel(entry.label) }}</span>
        </div>
      </div>
    </div>
    <section v-if="isTableMode" class="selection-summary" :aria-label="adminText('workflowsAll.gratis_selected_tables', 'Selected tables')">
      <div class="selection-header">
        <h2>{{ adminText('workflowsAll.gratis_selected_tables', 'Selected tables') }}</h2>
        <span class="selection-count">{{ form.tableSelections.length }}</span>
      </div>
      <div v-for="selection in form.tableSelections" :key="selection.locationId" class="selection-row" :class="{ 'selection-row--invalid': getSelectionError(selection) }">
        <div class="selection-info">
          <div class="selection-icon" aria-hidden="true"><v-icon :size="22">event_seat</v-icon></div>
          <div class="selection-details">
            <h3>{{ getTableTitle(selection.locationId) }}</h3>
            <p v-if="getTableLocation(selection.locationId)" class="selection-location">{{ getTableLocation(selection.locationId) }}</p>
            <p class="selection-category">{{ getSourceName(selection) }}</p>
          </div>
        </div>
        <div class="selection-quantity">
          <strong>{{ selection.quantity }}</strong>
          <span>{{ selection.quantity === 1 ? workflowCustomerLabel('sharedTableTicketSingular', {}, $t('sharedTableTicketSingular')) : workflowCustomerLabel('sharedTableTicketPlural', {}, $t('sharedTableTicketPlural')) }}</span>
        </div>
        <div class="selection-actions">
          <button class="selection-action selection-action--edit" type="button" :aria-label="adminText('workflowsAll.gratis_edit_table', 'Edit {{name}}', { name: getTableTitle(selection.locationId) })" @click="chooseTable(selection.locationId)">
            <v-icon :size="18">edit</v-icon><span>{{ adminText('workflowsAll.gratis_edit', 'Edit') }}</span>
          </button>
          <button class="selection-action selection-action--remove" type="button" :aria-label="adminText('workflowsAll.gratis_remove_table', 'Remove {{name}}', { name: getTableTitle(selection.locationId) })" @click="removeTable(selection.locationId)">
            <v-icon :size="18">delete</v-icon><span>{{ adminText('workflowsAll.gratis_remove', 'Remove') }}</span>
          </button>
        </div>
        <p v-if="getSelectionError(selection)" class="selection-error">{{ getSelectionError(selection) }}</p>
      </div>
      <p v-if="!form.tableSelections.length" class="selection-empty">{{ adminText('workflowsAll.gratis_select_table', 'Select a table on the floor plan to add gratis tickets.') }}</p>
      <div class="selection-total" aria-live="polite" aria-atomic="true">
        <span>{{ adminText('workflowsAll.gratis_total', 'Total tickets') }}</span>
        <div><strong>{{ selectedQuantity }}</strong><span> / {{ maxQuantity }}</span></div>
      </div>
    </section>
    <p v-else class="total-quantity" v-text="adminText('workflowsAll.gratis_selected_seats', 'Selected: {{selected}} / {{quantity}}', { selected: form.selectedSeats.length, quantity: form.initialQuantity })" />
    <v-dialog v-model="categoryDialog" max-width="500px">
      <v-card class="category-dialog">
        <v-container>
          <v-card-title primary-title>
            <span class="dialog-title">{{ adminText('workflowsAll.gratis_choose_category', 'Choose table category') }}</span>
          </v-card-title>
          <v-card-text>
            <select-field
              v-model="categorySourceId"
              :items="categoryCandidates"
              item-render="name"
              item-select="id"
              :label="adminText('workflowsAll.category_a3c686e', 'Category')"
            />
          </v-card-text>
        </v-container>
        <v-card-actions>
          <v-spacer />
          <main-button :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" style="margin-right: 12px" flat outline @click.prevent="categoryDialog = false" />
          <main-button :text="adminText('workflowsAll.next_bc98198', 'Next')" :disabled="!categorySourceId" @click.prevent="confirmCategory" />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <floor-plan-element-dialog
      v-if="dialogTicket"
      :key="dialogKey"
      :open-dialog="tableDialog"
      :order-item="dialogTicket"
      :quantity-limit="maxQuantity"
      :item-quantity-limit="availableDialogQuantity"
      :available-table-quantity="availableDialogQuantity"
      :current-selected-amount="selectedQuantity"
      :initial-quantity="dialogInitialQuantity"
      :is-initially-selected="!!dialogSelection"
      :existing-quantity="dialogSelection ? dialogSelection.quantity : 0"
      :show-cart-state="false"
      @closeDialog="tableDialog = false"
      @removeTicketFromOrder="tableDialog = false"
      @addTickets="addTable"
    />
    <simple-snackbar :open.sync="snackbarOpen" :message-prop="snackbarMessage" color="red" />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { SeatState, TicketType } from '@/types'
import * as Hammer from 'hammerjs'
import * as svgPanZoom from 'svg-pan-zoom'
import CustomRadioButton from '../events/custom-radio-button.vue'
import FloorPlanElementDialog from '../accreditations/register-stepper/floor-plan-element-dialog.vue'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import { parseTicketLocationDisplayFields } from '@/utils'
import { isSharedTable, isValidTableQuantity, getSharedTableQuantities } from '@/utils/floor-plan-quantity'

export default {
  mixins: [WorkflowDisplay],
  name: 'gratis-ticket-floor-plan',
  components: { CustomRadioButton, FloorPlanElementDialog, SimpleSnackbar },
  props: {
    initialVenue: { type: Object, default: () => ({}) },
    initVenueAvailability: { type: Object, default: () => ({}) },
    initialValues: { type: Object, required: true },
    orderItems: { type: Array, default: () => [] },
    maxQuantity: { type: Number, default: 40 },
  },
  data() {
    return {
      form: this.initialValues,
      svgDoc: null,
      panZoom: null,
      resizeFrame: null,
      mapAspectRatio: 1,
      loadingSvg: true,
      mapError: '',
      changeSelectOption: 1,
      categoryDialog: false,
      categorySourceId: null,
      dialogLocationId: null,
      dialogSourceId: null,
      dialogKey: 0,
      tableDialog: false,
      snackbarOpen: false,
      snackbarMessage: '',
      legend: [
        { label: 'workflowsAll.unavailable_2c9c1f7', color: '#D9D9D9' },
        { label: 'workflowsAll.available_7c62a14', color: '#4CAF50' },
        { label: 'workflowsAll.ticket_not_asssigned_disabled_4d8fbef', color: '#BD0202' },
        { label: 'workflowsAll.selected_9a976fc', color: 'white', border: '3px solid #333333' },
      ],
    }
  },
  computed: {
    isTableMode() { return this.form.ticketType === TicketType.TABLE },
    selectedQuantity() {
      return this.form.tableSelections.reduce((sum, selection) => sum + selection.quantity, 0)
    },
    sourceItems() { return this.orderItems.filter(item => !item.itemMeta.isGratis) },
    categoryCandidates() { return this.getLocationItems(this.dialogLocationId).filter(item => item.itemMeta.table) },
    dialogSource() { return this.sourceItems.find(item => item.id === this.dialogSourceId) },
    dialogTicket() {
      return this.dialogSource ? { ...this.dialogSource, price: '0', bundleOffer: null } : null
    },
    dialogSelection() { return this.form.tableSelections.find(selection => selection.locationId === this.dialogLocationId) },
    availableDialogQuantity() {
      if (!this.dialogSource) return 0

      return isSharedTable(this.dialogSource)
        ? Number((this.initVenueAvailability[this.dialogLocationId] || {}).availableCount || 0)
        : Number(this.dialogSource.itemMeta.max || 0)
    },
    dialogInitialQuantity() {
      if (this.dialogSelection && this.dialogSelection.sourceOrderItemId === this.dialogSourceId) return this.dialogSelection.quantity
      if (!this.dialogTicket) return 0
      if (isSharedTable(this.dialogTicket)) {
        return getSharedTableQuantities(this.dialogTicket, this.availableDialogQuantity, this.maxQuantity - this.selectedQuantity + (this.dialogSelection ? this.dialogSelection.quantity : 0))[0] || 0
      }

      return Math.max(1, Number(this.dialogTicket.itemMeta.min) || 0)
    },
  },
  watch: {
    initVenueAvailability() { this.refreshSeats() },
  },
  mounted() {
    window.addEventListener('resize', this.resizeMap)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeMap)
    window.cancelAnimationFrame(this.resizeFrame)
    if (this.panZoom) this.panZoom.destroy()
  },
  methods: {
    getLocationLabel(id) {
      const fields = parseTicketLocationDisplayFields(id, { language: 'en', isTable: true })

      return Object.entries(fields || {}).map(([label, value]) => `${label} ${value}`).join(', ')
    },
    getTableTitle(id) {
      const fields = parseTicketLocationDisplayFields(id, { language: 'en', isTable: true })

      return fields && fields.Table ? this.adminText('workflowsAll.gratis_table_name', 'Table {{number}}', { number: fields.Table }) : this.getLocationLabel(id)
    },
    getTableLocation(id) {
      const fields = parseTicketLocationDisplayFields(id, { language: 'en', isTable: true })
      if (!fields || !fields.Table) return ''

      return Object.entries(fields).filter(([label]) => label !== 'Table').map(([label, value]) => `${label} ${value}`).join(' · ')
    },
    getLocationItems(id) {
      return this.sourceItems.filter(item => (item.sectionSeats || []).includes(id))
    },
    isAvailable(id) {
      const state = this.initVenueAvailability[id]
      const items = this.getLocationItems(id)

      return !!(state && state.state === SeatState.AVAILABLE && state.ticketAssigned !== false && state.availableCount > 0 &&
        items.some(item => this.isTableMode ? item.itemMeta.table : !item.itemMeta.table && !item.itemMeta.parter))
    },
    getSourceName(selection) {
      const item = this.sourceItems.find(item => item.id === selection.sourceOrderItemId)

      return item ? item.name : this.adminText('workflowsAll.gratis_unavailable_category', 'Unavailable category')
    },
    getSelectionError(selection) {
      const ticket = this.sourceItems.find(item => item.id === selection.sourceOrderItemId && (item.sectionSeats || []).includes(selection.locationId))
      if (!this.isAvailable(selection.locationId) || !ticket) return this.adminText('workflowsAll.gratis_table_unavailable', 'This table is no longer available. Remove it or choose another table.')
      if (!isValidTableQuantity(ticket, selection.quantity, Number((this.initVenueAvailability[selection.locationId] || {}).availableCount))) return this.adminText('workflowsAll.gratis_quantity_changed', 'The quantity is no longer available for this table. Edit or remove it.')

      return ''
    },
    isSelectionValid() {
      return this.isTableMode
        ? this.selectedQuantity > 0 && this.selectedQuantity <= this.maxQuantity && this.form.tableSelections.every(selection => !this.getSelectionError(selection))
        : this.form.selectedSeats.length === this.form.initialQuantity && this.form.selectedSeats.every(this.isAvailable)
    },
    notify(message) { this.snackbarMessage = message; this.snackbarOpen = true },
    chooseTable(id) {
      if (!this.isAvailable(id)) return
      this.dialogLocationId = id
      const selection = this.dialogSelection
      const candidates = this.categoryCandidates
      if (candidates.length > 1) {
        this.categorySourceId = selection ? selection.sourceOrderItemId : candidates[0].id
        this.categoryDialog = true
      } else if (candidates.length) this.openTableDialog(candidates[0].id)
    },
    confirmCategory() {
      this.categoryDialog = false
      this.openTableDialog(this.categorySourceId)
    },
    openTableDialog(id) {
      this.dialogSourceId = id
      this.dialogKey += 1
      this.tableDialog = true
    },
    addTable(quantity) {
      const remaining = this.maxQuantity - this.selectedQuantity + (this.dialogSelection ? this.dialogSelection.quantity : 0)
      if (!this.isAvailable(this.dialogLocationId) || !isValidTableQuantity(this.dialogTicket, quantity, this.availableDialogQuantity, remaining)) {
        this.notify(this.adminText('workflowsAll.gratis_table_quantity_error', 'This quantity is not available for the selected table.'))

        return
      }
      this.form.tableSelections = this.form.tableSelections.filter(selection => selection.locationId !== this.dialogLocationId)
      this.form.tableSelections.push({ locationId: this.dialogLocationId, sourceOrderItemId: this.dialogSourceId, quantity })
      this.form.initialQuantity = this.selectedQuantity
      this.tableDialog = false
      this.paintMap()
    },
    removeTable(id) {
      this.form.tableSelections = this.form.tableSelections.filter(selection => selection.locationId !== id)
      this.form.initialQuantity = this.selectedQuantity
      this.paintMap()
    },
    refreshSeats() {
      this.tableDialog = false
      this.categoryDialog = false
      this.form.selectedSeats = this.form.selectedSeats.filter(this.isAvailable)
      this.paintMap()
    },
    onSvgError() { this.loadingSvg = false; this.mapError = this.adminText('workflowsAll.gratis_map_error', 'Unable to load the floor plan. Please reload this page.') },
    onSvgLoad() {
      try {
        const doc = this.$refs.svgfile.getSVGDocument()
        if (!doc || !doc.documentElement || doc.documentElement.tagName !== 'svg') return
        if (this.panZoom) this.panZoom.destroy()
        this.panZoom = null
        this.svgDoc = doc
        const viewBox = doc.documentElement.viewBox.baseVal
        const width = viewBox.width || parseFloat(doc.documentElement.getAttribute('width')) || 1
        const height = viewBox.height || parseFloat(doc.documentElement.getAttribute('height')) || width
        this.mapAspectRatio = height / width
        this.resizeMap()
        ;(this.initialVenue.seats || []).forEach(id => {
          const element = doc.getElementById(id)
          if (element) element.addEventListener('click', () => this.handleLocationClick(id))
        })
        this.paintMap()
      } catch (e) { this.onSvgError() }
    },
    resizeMap() {
      const object = this.$refs.svgfile
      if (!object) return
      object.style.height = `${Math.max(200, object.clientWidth * this.mapAspectRatio)}px`
      window.cancelAnimationFrame(this.resizeFrame)
      // The embedded SVG must finish resizing before pan/zoom measures its viewport.
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null
        const svg = this.svgDoc && this.svgDoc.documentElement
        if (!svg || !this.$refs.svgfile) return
        if (svg.clientWidth <= 0 || svg.clientHeight <= 0) return this.resizeMap()
        try {
          if (!this.panZoom) this.hammerInit()
          else {
            this.panZoom.resize()
            this.panZoom.fit()
            this.panZoom.center()
          }
          this.loadingSvg = false
        } catch (e) { this.onSvgError() }
      })
    },
    handleLocationClick(id) {
      if (this.isTableMode) return this.chooseTable(id)
      if (!this.isAvailable(id)) return
      const row = id.split('_').find(part => part.startsWith('Row-'))
      const section = id.split('_').find(part => part.startsWith('Section-'))
      const ids = this.changeSelectOption === 2 && row
        ? this.initialVenue.seats.filter(seat => seat.split('_').includes(row) && seat.split('_').find(part => part.startsWith('Section-')) === section && this.isAvailable(seat))
        : [id]
      if (this.form.selectedSeats.includes(id)) {
        this.form.selectedSeats = this.form.selectedSeats.filter(seat => !ids.includes(seat))
      } else {
        const additions = ids.filter(seat => !this.form.selectedSeats.includes(seat))
        if (this.form.selectedSeats.length + additions.length > this.form.initialQuantity) {
          this.notify(this.adminText('workflowsAll.gratis_selection_limit', 'This selection exceeds the defined ticket quantity.'))

          return
        }
        this.form.selectedSeats.push(...additions)
      }
      this.paintMap()
    },
    paintMap() {
      if (!this.svgDoc) return
      ;(this.initialVenue.seats || []).forEach(id => {
        const element = this.svgDoc.getElementById(id)
        if (!element) return
        const state = this.initVenueAvailability[id]
        const unavailable = state && (state.state === SeatState.HOLD || state.state === SeatState.SOLD)
        const disabled = !state || state.state === SeatState.DISABLED || state.ticketAssigned === false || !this.getLocationItems(id).length
        const selection = this.form.tableSelections.find(selection => selection.locationId === id)
        const selected = this.isTableMode ? !!selection : this.form.selectedSeats.includes(id)
        element.style.fill = selected
          ? (selection && this.getSelectionError(selection) ? '#BD0202' : 'white')
          : unavailable ? '#D9D9D9' : disabled ? '#BD0202' : this.isAvailable(id) ? '#4CAF50' : '#D9D9D9'
        element.style.cursor = this.isAvailable(id) ? 'pointer' : 'default'
        if (selected) {
          element.setAttribute('stroke', '#333333')
          element.setAttribute('stroke-width', element.hasAttribute('r') ? Number(element.getAttribute('r')) * 0.4 : 2)
        } else element.removeAttribute('stroke')
      })
    },
    hammerInit: function () {
      let eventsHandler
      eventsHandler = {
        haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
        init: function (options) {
          let instance = options.instance,
            initialScale = 1,
            pannedX = 0,
            pannedY = 0
          this.hammer = Hammer(options.svgElement, {
            inputClass: Hammer.SUPPORT_POINTER_EVENTS
              ? Hammer.PointerEventInput
              : Hammer.TouchInput,
          })
          this.hammer.get('pinch').set({
            enable: true,
          })
          this.hammer.on('doubletap', function () {
            instance.zoomIn()
          })
          this.hammer.on('panstart panmove', function (ev) {
            if (ev.type === 'panstart') {
              pannedX = 0
              pannedY = 0
            }
            instance.panBy({
              x: ev.deltaX - pannedX,
              y: ev.deltaY - pannedY,
            })
            pannedX = ev.deltaX
            pannedY = ev.deltaY
          })
          this.hammer.on('pinchstart pinchmove', function (ev) {
            if (ev.type === 'pinchstart') {
              initialScale = instance.getZoom()
              instance.zoomAtPoint(initialScale * ev.scale, {
                x: ev.center.x,
                y: ev.center.y,
              })
            }
            instance.zoomAtPoint(initialScale * ev.scale, {
              x: ev.center.x,
              y: ev.center.y,
            })
          })
          options.svgElement.addEventListener('touchmove', function (e) {
            e.preventDefault()
          })
        },
        destroy: function () {
          this.hammer.destroy()
        },
      }
      this.panZoom = svgPanZoom(this.svgDoc.documentElement, {
        zoomEnabled: true,
        controlIconsEnabled: false,
        maxZoom: 3,
        fit: 1,
        center: 1,
        customEventsHandler: eventsHandler,
      })
    },
  },
}
</script>

<style scoped>
.floor-plan {
  width: 100%;
  min-width: 0;
  height: fit-content;
  border: 2px solid #f5f5f5;
  border-radius: 8px;
}

.floor-plan-title {
  width: 100%;
  height: 3.5rem;
  background: #f5f5f5;
  font-family: 'Raleway', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #666666;
  display: flex;
  align-items: center;
  text-indent: 1.5rem;
}

.lower-container {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-top: 1.5rem;
  column-gap: 1.5rem;
  width: 100%;
}

#spinner-container {
  height: 600px;
}

#spinner-svg-wrapper {
  min-height: 200px;
  overflow: hidden;
  padding: 1rem;
}

.seat-state-wrapper {
  width: fit-content;
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  border-top: 2px solid #f5f5f5;
  padding: 1rem;
  column-gap: 0.5rem;
  gap: 6px;
}

.state-logo {
  height: 1rem;
  width: 1rem;
  flex-shrink: 0;
  align-self: center;
  background: black;
  border-radius: 100rem;
}

span {
  color: var(--Grey-10, #666);
}

.seat-state {
  display: flex;
  align-items: flex-start;
  column-gap: 0.5rem;
  padding: 0.2rem 0.5rem;
}

.count {
  color: #0277bd;
  display: flex;
  padding: 2px;
  margin: 0;
  align-items: center;
  gap: 8px;
  font-family: 'Source Sans Pro';
  font-size: 14px;
  font-style: normal;
  border-radius: 2px;
  font-weight: 400;
  line-height: 150%;
}

.radio-buttons-container {
  display: flex;
  margin: 16px 20px 16px;
}

@media screen and (max-width: 1012px) {
  .lower-container {
    flex-direction: column;
    width: 100%;
  }

  .radio-buttons-container {
    flex-wrap: wrap;
    row-gap: 1rem;
  }
}
.category-dialog { padding-bottom: 1.5rem !important; padding-right: 1.5rem !important; }
.selection-summary { margin-top: 24px; min-width: 0; }
.selection-header { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.selection-header h2 { margin: 0; font-size: 16px; font-weight: 600; color: #333; }
.selection-count { display: inline-flex; justify-content: center; align-items: center; min-width: 24px; height: 24px; padding: 0 7px; border-radius: 12px; background: #eef5fa; color: #0277bd; font-size: 12px; font-weight: 600; }
.selection-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 16px; padding: 16px; margin-bottom: 12px; background: #fff; border: 1px solid #e2e8ed; border-radius: 12px; }
.selection-row--invalid { border-color: #db384c; }
.selection-info { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
.selection-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 40px; height: 40px; border-radius: 10px; background: #eef5fa; }
.selection-icon .v-icon { color: #0277bd; }
.selection-details { min-width: 0; overflow-wrap: anywhere; }
.selection-details h3 { margin: 0 0 4px; font-size: 16px; line-height: 1.4; font-weight: 600; color: #333; }
.selection-details p { margin: 0; font-size: 13px; line-height: 1.5; }
.selection-location { color: #666; }
.selection-category { color: #777; }
.selection-quantity { display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 64px; padding: 8px 12px; background: #f5f8fa; border-radius: 10px; }
.selection-quantity strong { font-size: 22px; line-height: 1.2; font-weight: 600; color: #333; }
.selection-quantity span { font-size: 12px; line-height: 1.5; color: #666; }
.selection-actions { display: flex; justify-content: flex-end; gap: 8px; grid-column: 1 / -1; }
.selection-action { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 44px; padding: 0 16px; border: 1px solid; border-radius: 8px; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; background: #fff; }
.selection-action span, .selection-action .v-icon { color: inherit; }
.selection-action--edit { color: #0277bd; border-color: #c4ddec; }
.selection-action--edit:hover { background: #eef5fa; }
.selection-action--remove { color: #c72c40; border-color: #f0cbd0; }
.selection-action--remove:hover { background: #fff3f4; }
.selection-action:focus-visible { outline: 3px solid #0277bd; outline-offset: 3px; }
.selection-error, .map-error { color: #bd0202; width: 100%; overflow-wrap: anywhere; }
.selection-error { grid-column: 1 / -1; margin: 0; font-size: 13px; line-height: 1.5; }
.selection-empty { padding: 20px 16px; border: 1px dashed #cfdde6; border-radius: 12px; color: #666; font-size: 14px; line-height: 1.5; }
.selection-total { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px; border-radius: 10px; background: #eef5fa; }
.selection-total > span { font-size: 14px; font-weight: 600; color: #333; }
.selection-total strong { color: #0277bd; font-size: 22px; font-weight: 600; }
.selection-total div { white-space: nowrap; }
.selection-total div span { font-size: 14px; color: #666; }
.total-quantity { font-weight: 600; margin-top: 1rem; }
@media screen and (min-width: 960px) {
  .selection-row { grid-template-columns: minmax(0, 1fr) auto auto; }
  .selection-actions { grid-column: auto; }
}
@media screen and (max-width: 600px) {
  #spinner-svg-wrapper { padding: 8px; }
  .floor-plan-title { height: 48px; font-size: 18px; text-indent: 16px; }
  .seat-state-wrapper { width: 100%; padding: 12px; gap: 8px 12px; }
  .seat-state { padding: 0; font-size: 12px; min-width: 0; }
  .seat-state span { overflow-wrap: anywhere; }
  .selection-row { padding: 12px; gap: 12px; }
  .selection-info { gap: 8px; }
  .selection-icon { flex-basis: 32px; height: 32px; border-radius: 8px; }
  .selection-quantity { min-width: 56px; padding: 8px; }
  .selection-actions .selection-action { flex: 1; min-width: 0; padding: 0 12px; }
}
</style>
